import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  SignOut,
  CurrencyDollar,
  Warning,
  FileCsv,
  FilePdf,
  CalendarBlank,
  UploadSimple,
  Users,
  Sun,
  Moon,
  Keyboard,
  EyeSlash,
  List,
  X,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import ImportDialog from '../components/ImportDialog';
import { ClientDialogs } from '../components/ClientTable';
import SegmentationTab from '../components/SegmentationTab';
import MonthlyBreakdown from '../components/MonthlyBreakdown';
import FYClosingReport from '../components/FYClosingReport';
import QuarterlyReport from '../components/QuarterlyReport';
import TeamManagement from '../components/TeamManagement';
import NotificationCenter from '../components/NotificationCenter';
import ActivityLog from '../components/ActivityLog';
import GoalTracking from '../components/GoalTracking';
import ClientComparison from '../components/ClientComparison';
import AdvancedAnalytics from '../components/AdvancedAnalytics';
import SmartAlerts from '../components/SmartAlerts';
import MonthComparison from '../components/MonthComparison';
import DashboardSharing from '../components/DashboardSharing';
import OverviewTab from '../components/OverviewTab';
import SettingsDialog from '../components/SettingsDialog';
import ReportsTab from '../components/ReportsTab';
import DeleteByRange from '../components/DeleteByRange';
import UniversalSearch from '../components/UniversalSearch';
import CohortAnalysis from '../components/CohortAnalysis';

// Lazy load non-critical boards (only loaded when user navigates to them)
const MasterDashboard = lazy(() => import('../components/MasterDashboard'));
const ExpensesDashboard = lazy(() => import('../components/ExpensesDashboard'));
const PurchasesDashboard = lazy(() => import('../components/PurchasesDashboard'));
import PartyWiseSales from '../components/PartyWiseSales';
import BulkSalesEntry from '../components/BulkSalesEntry';
import { usePrivacy } from '../contexts/PrivacyContext';
import { SalesSkeleton, ExpensesSkeleton, PurchasesSkeleton, MasterSkeleton } from '../components/DashboardSkeleton';
import { useLiveSocket } from '../hooks/useLiveSocket';
import { BACKEND_URL } from '../config';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const canWrite = user?.role === 'admin' || user?.role === 'manager';
  const isAdmin = user?.role === 'admin';
  const [analytics, setAnalytics] = useState(null);
  const [selectedRevenueFY, setSelectedRevenueFY] = useState('all');
  const [clients, setClients] = useState([]);
  const [allClientNames, setAllClientNames] = useState([]);
  const [segmentation, setSegmentation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addSaleOpen, setAddSaleOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [deleteClientOpen, setDeleteClientOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [drilldownClientId, setDrilldownClientId] = useState(null);
  const { privacyMode, togglePrivacy, maskCurrency } = usePrivacy();
  const [widgetOrder, setWidgetOrder] = useState(['statsCards', 'revenueChart_quickActions', 'forecast', 'clientTable']);
  const [activeBoard, setActiveBoard] = useState('sales');
  const [activeSalesTab, setActiveSalesTab] = useState('overview');
  const [searchOpen, setSearchOpen] = useState(false);
  const fetchDataRef = useRef(null);

  // Live WebSocket for real-time data refresh
  const handleLiveUpdate = useCallback(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);
  const { connected: liveConnected } = useLiveSocket(handleLiveUpdate);

  // Load widget order
  useEffect(() => {
    const loadOrder = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/user/widget-order`, { withCredentials: true });
        if (data.widget_order?.length) setWidgetOrder(data.widget_order);
      } catch (err) { console.error('Failed to load widget order:', err.message); }
    };
    loadOrder();
  }, []);

  const [newClient, setNewClient] = useState({ name: '', email: '', company: '', industry: '', region: '' });
  const [newSale, setNewSale] = useState({ client_id: '', amount: '', description: '', date: '' });
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(20);
  const [summaryFrequency, setSummaryFrequency] = useState('off');
  const [sendingSummary, setSendingSummary] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkSaleOpen, setBulkSaleOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Client pagination state
  const [clientPage, setClientPage] = useState(1);
  const [clientSearch, setClientSearch] = useState('');
  const [clientSort, setClientSort] = useState('name_asc');
  const [clientTotal, setClientTotal] = useState(0);
  const [clientTotalPages, setClientTotalPages] = useState(1);

  // Widget visibility toggles
  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : {
      statsCards: true,
      revenueChart: true,
      quickActions: true,
      forecast: true,
      clientTable: true,
    };
  });

  const toggleWidget = (key) => {
    setWidgets(prev => {
      const updated = { ...prev, [key]: !prev[key] };
      localStorage.setItem('dashboard_widgets', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    if (user) {
      setEmailNotifications(user.email_notifications !== false);
      setAlertThreshold(user.alert_threshold || 20);
      setSummaryFrequency(user.summary_frequency || 'off');
    }
  }, [user]);

  useEffect(() => {
    const dippingClients = clients.filter((c) => c.is_dipping);
    if (dippingClients.length > 0) {
      const names = dippingClients.slice(0, 3).map(c => c.name).join(', ');
      const extra = dippingClients.length > 3 ? ` +${dippingClients.length - 3} more` : '';
      toast.error(`${dippingClients.length} Sales Alert${dippingClients.length > 1 ? 's' : ''}`, {
        description: `${names}${extra} — sales dropped by more than 20%`,
        duration: 6000,
        id: 'dipping-alerts',
      });
    }
  }, [clients]);

  // Keyboard shortcuts
  const handleKeyboard = useCallback((e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

    if (e.key === 'n' && canWrite) { e.preventDefault(); setAddSaleOpen(true); }
    else if (e.key === 'c' && canWrite) { e.preventDefault(); setAddClientOpen(true); }
    else if (e.key === 'i' && canWrite) { e.preventDefault(); setImportOpen(true); }
    else if (e.key === 's') { e.preventDefault(); setSettingsOpen(true); }
    else if (e.key === 'd') { e.preventDefault(); toggleTheme(); }
    else if (e.key === '?') { e.preventDefault(); setShortcutsOpen(true); }
    else if (e.key === 'Escape') {
      setAddSaleOpen(false); setAddClientOpen(false); setSettingsOpen(false);
      setEditClientOpen(false); setDeleteClientOpen(false); setBulkDeleteOpen(false);
      setShortcutsOpen(false);
    }
  }, [canWrite, toggleTheme]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [handleKeyboard]);

  const fetchClients = useCallback(async (pg, search, sort) => {
    try {
      const params = { page: pg || clientPage, limit: 50, search: search ?? clientSearch, sort: sort || clientSort };
      const { data } = await axios.get(`${BACKEND_URL}/api/clients`, { params, withCredentials: true });
      setClients(data.clients || []);
      setClientTotal(data.total || 0);
      setClientTotalPages(data.pages || 1);
    } catch (err) { console.error('Failed to fetch clients:', err.message); }
  }, [clientPage, clientSearch, clientSort]);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      // Phase 1: Load critical data first (analytics + clients) to unblock UI fast
      const [analyticsRes, clientNamesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/analytics`, { params, withCredentials: true }).catch(e => { console.error('Analytics API failed:', e.message); return { data: null }; }),
        axios.get(`${BACKEND_URL}/api/clients/names`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
      setAllClientNames(clientNamesRes.data || []);
      setLoading(false); // Unblock UI immediately after critical data

      // Phase 2: Load secondary data in background (non-blocking)
      await fetchClients(1, '', 'name_asc');
      const [segmentationRes, forecastRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/segmentation`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/analytics/forecast`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      if (segmentationRes.data) setSegmentation(segmentationRes.data);
      setForecast(forecastRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false); // Always unblock even on error
    }
  }, [startDate, endDate, fetchClients]);
  fetchDataRef.current = fetchData;

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/clients`, newClient, { withCredentials: true });
      toast.success('Client added successfully');
      setAddClientOpen(false);
      setNewClient({ name: '', email: '', company: '', industry: '', region: '' });
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to add client';
      toast.error(`Failed to add client: ${errorMsg}`);
    }
  };

  const handleAddSale = async (e) => {
    e.preventDefault();
    try {
      const salePayload = { client_id: newSale.client_id, amount: parseFloat(newSale.amount), description: newSale.description };
      if (newSale.date) salePayload.date = new Date(newSale.date).toISOString();
      await axios.post(`${BACKEND_URL}/api/sales`, salePayload, { withCredentials: true });
      toast.success('Sale recorded successfully');
      setAddSaleOpen(false);
      setNewSale({ client_id: '', amount: '', description: '', date: '' });
      fetchData();
    } catch (error) { toast.error('Failed to record sale'); }
  };

  const handleUpdateSettings = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/user/settings`, { email_notifications: emailNotifications, alert_threshold: parseFloat(alertThreshold), summary_frequency: summaryFrequency }, { withCredentials: true });
      toast.success('Settings updated');
      setSettingsOpen(false);
      fetchData();
    } catch (error) { toast.error('Failed to update settings'); }
  };

  const handleSendSummaryNow = async () => {
    setSendingSummary(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/summary/send`, {}, { withCredentials: true });
      toast.success(`Summary sent to ${data.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send summary');
    } finally {
      setSendingSummary(false);
    }
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${BACKEND_URL}/api/clients/${selectedClient.id}`, selectedClient, { withCredentials: true });
      toast.success('Client updated successfully');
      setEditClientOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) { toast.error('Failed to update client'); }
  };

  const handleDeleteClient = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${selectedClient.id}`, { withCredentials: true });
      toast.success('Client deleted successfully');
      setDeleteClientOpen(false);
      setSelectedClient(null);
      fetchData();
    } catch (error) { toast.error('Failed to delete client'); }
  };

  const handleBulkDelete = async () => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/clients/bulk-delete`, selectedClientIds, { withCredentials: true });
      toast.success(`Deleted ${data.clients_deleted} clients and ${data.sales_deleted} sales transactions`);
      setBulkDeleteOpen(false);
      setSelectedClientIds([]);
      fetchData();
    } catch (error) { toast.error('Failed to delete clients'); }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'sales_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully');
    } catch (error) { toast.error('Failed to export CSV'); }
  };

  const handleExportPDF = async () => {
    try {
      const params = {};
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();
      const response = await axios.get(`${BACKEND_URL}/api/export/pdf`, { params, withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'analytics_report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(startDate || endDate ? 'Date-range PDF exported' : 'PDF exported successfully');
    } catch (error) { toast.error('Failed to export PDF'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <SalesSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container bg-background min-h-screen">
      <header className="border-b border-border bg-card/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl tracking-tight font-bold truncate">Business Analytics</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-muted-foreground hidden sm:block">SALES PERFORMANCE MONITOR</p>
              {liveConnected && (
                <span className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400" data-testid="live-indicator">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  LIVE
                </span>
              )}
            </div>
          </div>

          {/* Desktop header actions */}
          <div className="hidden md:flex items-center gap-2 lg:gap-4">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-muted/50 border border-border rounded-sm hover:bg-muted transition-colors"
              data-testid="search-trigger-btn"
            >
              <MagnifyingGlass size={14} weight="regular" />
              <span className="hidden lg:inline">Search</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 bg-background border border-border rounded-sm">
                <span className="text-xs">⌘</span>K
              </kbd>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground hidden lg:inline">{user?.email}</span>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-bold tracking-wide uppercase ${
                user?.role === 'admin' ? 'bg-primary text-primary-foreground' :
                user?.role === 'manager' ? 'bg-accent text-accent-foreground' :
                'bg-muted text-muted-foreground'
              }`} data-testid="user-role-badge">
                {user?.role || 'viewer'}
              </span>
            </div>
            <Button data-testid="theme-toggle" variant="outline" size="sm" className="rounded-sm" onClick={toggleTheme} title={theme === 'light' ? 'Switch to dark mode (D)' : 'Switch to light mode (D)'}>
              {theme === 'light' ? <Moon size={16} weight="regular" /> : <Sun size={16} weight="regular" />}
            </Button>
            <Button data-testid="shortcuts-button" variant="outline" size="sm" className="rounded-sm" onClick={() => setShortcutsOpen(true)} title="Keyboard shortcuts (?)">
              <Keyboard size={16} weight="regular" />
            </Button>
            <Button data-testid="privacy-toggle" variant={privacyMode ? "default" : "outline"} size="sm" className="rounded-sm" onClick={togglePrivacy} title={privacyMode ? "Show figures" : "Hide figures"}>
              <EyeSlash size={16} weight={privacyMode ? "fill" : "regular"} />
            </Button>
            <NotificationCenter />
            <SettingsDialog
              open={settingsOpen}
              onOpenChange={setSettingsOpen}
              emailNotifications={emailNotifications}
              setEmailNotifications={setEmailNotifications}
              alertThreshold={alertThreshold}
              setAlertThreshold={setAlertThreshold}
              summaryFrequency={summaryFrequency}
              setSummaryFrequency={setSummaryFrequency}
              sendingSummary={sendingSummary}
              onSendSummaryNow={handleSendSummaryNow}
              widgets={widgets}
              toggleWidget={toggleWidget}
              isAdmin={isAdmin}
              onSave={handleUpdateSettings}
            />
            <Button data-testid="logout-button" onClick={handleLogout} variant="outline" size="sm" className="rounded-sm">
              <SignOut size={16} weight="regular" className="mr-2" />
              Sign Out
            </Button>
          </div>

          {/* Mobile header: search + role badge + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => setSearchOpen(true)} data-testid="mobile-search-btn">
              <MagnifyingGlass size={18} weight="regular" />
            </Button>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold tracking-wide uppercase ${
              user?.role === 'admin' ? 'bg-primary text-primary-foreground' :
              user?.role === 'manager' ? 'bg-accent text-accent-foreground' :
              'bg-muted text-muted-foreground'
            }`}>{user?.role || 'viewer'}</span>
            <Button variant="outline" size="sm" className="rounded-sm h-9 w-9 p-0" onClick={() => setMobileMenuOpen(true)} data-testid="mobile-menu-button">
              <List size={20} weight="bold" />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile slide-out menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-72 p-0">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="text-left text-base tracking-tight">Menu</SheetTitle>
          </SheetHeader>
          <div className="p-4 space-y-1">
            <div className="px-3 py-2 mb-3 text-xs text-muted-foreground truncate">{user?.email}</div>
            <button onClick={() => { toggleTheme(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-theme-toggle">
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
              {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
            </button>
            <button onClick={() => { togglePrivacy(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-privacy-toggle">
              <EyeSlash size={18} weight={privacyMode ? "fill" : "regular"} />
              {privacyMode ? 'Show Figures' : 'Hide Figures'}
            </button>
            <button onClick={() => { setSettingsOpen(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-settings-button">
              <Keyboard size={18} />
              Settings
            </button>
            {canWrite && (
              <button onClick={() => { setImportOpen(true); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-import-button">
                <UploadSimple size={18} />
                Import Data
              </button>
            )}
            <button onClick={() => { handleExportCSV(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-export-csv">
              <FileCsv size={18} />
              Export CSV
            </button>
            <button onClick={() => { handleExportPDF(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm hover:bg-muted transition-colors" data-testid="mobile-export-pdf">
              <FilePdf size={18} />
              Export PDF
            </button>
            <div className="border-t border-border my-3" />
            <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm text-destructive hover:bg-destructive/10 transition-colors" data-testid="mobile-logout-button">
              <SignOut size={18} />
              Sign Out
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Board Navigation */}
        <div className="mb-4 sm:mb-6 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-1 p-1 bg-card border border-border rounded-sm" data-testid="board-navigation">
            {[
              { key: 'master', label: 'Master', color: 'text-primary' },
              { key: 'sales', label: 'Sales', color: 'text-accent' },
              { key: 'expenses', label: 'Expenses', color: 'text-destructive' },
              { key: 'purchases', label: 'Purchases', color: 'text-orange-600 dark:text-orange-400' },
            ].map(b => (
              <button
                key={b.key}
                onClick={() => setActiveBoard(b.key)}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold rounded-sm transition-all ${
                  activeBoard === b.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
                data-testid={`board-nav-${b.key}`}
              >
                {b.label}
              </button>
            ))}
          </div>
          {canWrite && <DeleteByRange onDeleted={fetchData} />}
        </div>

        {/* Sales Board */}
        {activeBoard === 'sales' && (
          <>
            {/* Toolbar: date filters + actions */}
            <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-card border border-border rounded-sm px-3 py-2 flex-1 sm:flex-initial">
                  <CalendarBlank size={16} weight="regular" className="shrink-0" />
                  <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} selectsStart startDate={startDate} endDate={endDate} placeholderText="Start Date" className="text-sm w-full sm:w-28 outline-none bg-transparent" data-testid="start-date-picker" />
                </div>
                <div className="flex items-center gap-2 bg-card border border-border rounded-sm px-3 py-2 flex-1 sm:flex-initial">
                  <CalendarBlank size={16} weight="regular" className="shrink-0" />
                  <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} placeholderText="End Date" className="text-sm w-full sm:w-28 outline-none bg-transparent" data-testid="end-date-picker" />
                </div>
                {(startDate || endDate) && (
                  <Button onClick={() => { setStartDate(null); setEndDate(null); }} variant="outline" size="sm" className="rounded-sm" data-testid="clear-date-filter">Clear</Button>
                )}
              </div>
              <div className="hidden md:flex ml-auto gap-2">
                {canWrite && (
                  <Button onClick={() => setImportOpen(true)} variant="outline" size="sm" className="rounded-sm" data-testid="import-data-button">
                    <UploadSimple size={16} weight="regular" className="mr-2" />
                    Import Data
                  </Button>
                )}
                <Button onClick={handleExportCSV} variant="outline" size="sm" className="rounded-sm" data-testid="export-csv-button">
                  <FileCsv size={16} weight="regular" className="mr-2" />
                  Export CSV
                </Button>
                <Button onClick={handleExportPDF} variant="outline" size="sm" className="rounded-sm" data-testid="export-pdf-button">
                  <FilePdf size={16} weight="regular" className="mr-2" />
                  {startDate || endDate ? 'Date Range PDF' : 'Export PDF'}
                </Button>
                <DashboardSharing canWrite={canWrite} />
              </div>
            </div>

            <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImportSuccess={fetchData} />

            {widgets.statsCards && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4 sm:mb-8">
              <button onClick={() => setActiveSalesTab('reports')} data-testid="revenue-card" className="stat-card bg-card border border-border rounded-sm p-4 sm:p-6 h-full text-left hover:border-primary/50 transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-sm bg-primary flex items-center justify-center">
                    <CurrencyDollar size={18} weight="bold" className="text-primary-foreground" />
                  </div>
                  {analytics?.fy_summaries?.length > 0 && (
                    <select
                      value={selectedRevenueFY}
                      onChange={(e) => { e.stopPropagation(); setSelectedRevenueFY(e.target.value); }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-[10px] sm:text-xs border border-border rounded-sm bg-background px-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="revenue-fy-select"
                    >
                      <option value="all">All Time</option>
                      {analytics.fy_summaries.map(s => (
                        <option key={s.financial_year} value={s.financial_year}>{s.financial_year}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1 sm:mb-2">
                  {selectedRevenueFY === 'all' ? 'Total Revenue' : `Revenue ${selectedRevenueFY}`}
                </div>
                <div className="text-lg sm:text-3xl font-bold tracking-tight">
                  {maskCurrency(
                    selectedRevenueFY === 'all'
                      ? (analytics?.total_revenue || 0)
                      : (analytics?.fy_summaries?.find(s => s.financial_year === selectedRevenueFY)?.total_revenue || 0)
                  )}
                </div>
                {selectedRevenueFY !== 'all' && (
                  <div className="text-[10px] text-muted-foreground mt-1" data-testid="revenue-fy-txn-count">
                    {analytics?.fy_summaries?.find(s => s.financial_year === selectedRevenueFY)?.transactions || 0} transactions
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground mt-1">Click for reports</div>
              </button>

              <button onClick={() => setActiveSalesTab('partywise')} data-testid="clients-card" className="stat-card bg-card border border-border rounded-sm p-4 sm:p-6 h-full text-left hover:border-primary/50 transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-sm bg-accent flex items-center justify-center">
                    <Users size={18} weight="bold" className="text-accent-foreground" />
                  </div>
                </div>
                <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1 sm:mb-2">Active Clients</div>
                <div className="text-lg sm:text-3xl font-bold tracking-tight">{privacyMode ? '••••' : (analytics?.active_clients || 0)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">Click for party-wise</div>
              </button>

              <button onClick={() => setActiveSalesTab('alerts')} data-testid="dips-card" className="stat-card bg-card border border-border rounded-sm p-4 sm:p-6 h-full text-left hover:border-primary/50 transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-sm bg-destructive flex items-center justify-center">
                    <Warning size={18} weight="bold" className="text-destructive-foreground" />
                  </div>
                </div>
                <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1 sm:mb-2">Sales Alerts</div>
                <div className="text-lg sm:text-3xl font-bold tracking-tight text-destructive">{privacyMode ? '••••' : (analytics?.dipping_clients || 0)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">Click for alerts</div>
              </button>
            </div>
            )}

            <Tabs value={activeSalesTab} onValueChange={setActiveSalesTab} className="space-y-4 sm:space-y-6">
              <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                <TabsList className="bg-card border border-border rounded-sm inline-flex w-auto min-w-max">
                  <TabsTrigger value="overview" className="rounded-sm text-xs sm:text-sm" data-testid="overview-tab">Overview</TabsTrigger>
                  <TabsTrigger value="reports" className="rounded-sm text-xs sm:text-sm" data-testid="reports-tab">Reports</TabsTrigger>
                  <TabsTrigger value="monthly" className="rounded-sm text-xs sm:text-sm" data-testid="monthly-tab">Monthly</TabsTrigger>
                  <TabsTrigger value="goals" className="rounded-sm text-xs sm:text-sm" data-testid="goals-tab">Goals</TabsTrigger>
                  <TabsTrigger value="compare" className="rounded-sm text-xs sm:text-sm" data-testid="compare-tab">Compare</TabsTrigger>
                  <TabsTrigger value="alerts" className="rounded-sm text-xs sm:text-sm" data-testid="alerts-tab">Alerts</TabsTrigger>
                  <TabsTrigger value="analytics" className="rounded-sm text-xs sm:text-sm" data-testid="analytics-tab">Analytics</TabsTrigger>
                  <TabsTrigger value="segmentation" className="rounded-sm text-xs sm:text-sm" data-testid="segmentation-tab">Segments</TabsTrigger>
                  <TabsTrigger value="partywise" className="rounded-sm text-xs sm:text-sm" data-testid="partywise-tab">Party-Wise</TabsTrigger>
                  <TabsTrigger value="cohorts" className="rounded-sm text-xs sm:text-sm" data-testid="cohorts-tab">Cohorts</TabsTrigger>
                  <TabsTrigger value="activity" className="rounded-sm text-xs sm:text-sm" data-testid="activity-tab">Activity</TabsTrigger>
                  {isAdmin && <TabsTrigger value="team" className="rounded-sm text-xs sm:text-sm" data-testid="team-tab">Team</TabsTrigger>}
                </TabsList>
              </div>

              <TabsContent value="overview" className="space-y-6">
                <OverviewTab
                  analytics={analytics}
                  clients={clients}
                  allClientNames={allClientNames}
                  forecast={forecast}
                  widgets={widgets}
                  widgetOrder={widgetOrder}
                  setWidgetOrder={setWidgetOrder}
                  privacyMode={privacyMode}
                  maskCurrency={maskCurrency}
                  canWrite={canWrite}
                  onDataRefresh={fetchData}
                  drilldownClientId={drilldownClientId}
                  setDrilldownClientId={setDrilldownClientId}
                  selectedClientIds={selectedClientIds}
                  setSelectedClientIds={setSelectedClientIds}
                  onBulkDeleteOpen={() => setBulkDeleteOpen(true)}
                  onEditClient={(client) => { setSelectedClient(client); setEditClientOpen(true); }}
                  onDeleteClient={(client) => { setSelectedClient(client); setDeleteClientOpen(true); }}
                  clientTotal={clientTotal}
                  clientTotalPages={clientTotalPages}
                  clientPage={clientPage}
                  onPageChange={(p) => { setClientPage(p); fetchClients(p, clientSearch, clientSort); }}
                  onSearchChange={(s) => { setClientSearch(s); setClientPage(1); fetchClients(1, s, clientSort); }}
                  onSortChange={(s) => { setClientSort(s); setClientPage(1); fetchClients(1, clientSearch, s); }}
                  addClientOpen={addClientOpen}
                  setAddClientOpen={setAddClientOpen}
                  addSaleOpen={addSaleOpen}
                  setAddSaleOpen={setAddSaleOpen}
                  newClient={newClient}
                  setNewClient={setNewClient}
                  newSale={newSale}
                  setNewSale={setNewSale}
                  onAddClient={handleAddClient}
                  onAddSale={handleAddSale}
                  bulkSaleOpen={bulkSaleOpen}
                  setBulkSaleOpen={setBulkSaleOpen}
                />
              </TabsContent>

              <TabsContent value="reports" className="space-y-6">
                <ReportsTab />
              </TabsContent>

              <TabsContent value="segmentation">
                <SegmentationTab segmentation={segmentation} />
              </TabsContent>

              <TabsContent value="monthly">
                <MonthlyBreakdown canWrite={canWrite} clients={allClientNames} />
                <QuarterlyReport />
                <FYClosingReport />
              </TabsContent>

              <TabsContent value="goals">
                <GoalTracking canWrite={canWrite} clients={allClientNames} />
              </TabsContent>

              <TabsContent value="compare" className="space-y-8">
                <ClientComparison clients={allClientNames} />
                <div className="border-t border-border pt-6">
                  <MonthComparison />
                </div>
              </TabsContent>

              <TabsContent value="alerts">
                <SmartAlerts onClientClick={(id) => setDrilldownClientId(id)} />
              </TabsContent>

              <TabsContent value="analytics">
                <AdvancedAnalytics />
              </TabsContent>

              <TabsContent value="partywise">
                <PartyWiseSales />
              </TabsContent>

              <TabsContent value="cohorts">
                <CohortAnalysis />
              </TabsContent>

              <TabsContent value="activity">
                <ActivityLog />
              </TabsContent>

              {isAdmin && (
                <TabsContent value="team">
                  <TeamManagement currentUser={user} />
                </TabsContent>
              )}
            </Tabs>
          </>
        )}

        {/* Expenses Board */}
        {activeBoard === 'expenses' && (
          <Suspense fallback={<ExpensesSkeleton />}>
            <ExpensesDashboard canWrite={canWrite} />
          </Suspense>
        )}

        {/* Purchases Board */}
        {activeBoard === 'purchases' && (
          <Suspense fallback={<PurchasesSkeleton />}>
            <PurchasesDashboard canWrite={canWrite} />
          </Suspense>
        )}

        {/* Master Board */}
        {activeBoard === 'master' && (
          <Suspense fallback={<MasterSkeleton />}>
            <MasterDashboard onBoardChange={setActiveBoard} />
          </Suspense>
        )}
      </main>

      <ClientDialogs
        editClientOpen={editClientOpen}
        setEditClientOpen={setEditClientOpen}
        deleteClientOpen={deleteClientOpen}
        setDeleteClientOpen={setDeleteClientOpen}
        bulkDeleteOpen={bulkDeleteOpen}
        setBulkDeleteOpen={setBulkDeleteOpen}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
        selectedClientIds={selectedClientIds}
        onEditSubmit={handleEditClient}
        onDeleteConfirm={handleDeleteClient}
        onBulkDeleteConfirm={handleBulkDelete}
      />

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl tracking-tight">Keyboard Shortcuts</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {[
              { key: '⌘K', desc: 'Universal Search' },
              { key: 'N', desc: 'New Sale' },
              { key: 'C', desc: 'New Client' },
              { key: 'I', desc: 'Import Data' },
              { key: 'S', desc: 'Open Settings' },
              { key: 'D', desc: 'Toggle Dark/Light Mode' },
              { key: '?', desc: 'Show Shortcuts' },
              { key: 'Esc', desc: 'Close Dialogs' },
            ].map(({ key, desc }) => (
              <div key={key} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">{desc}</span>
                <kbd className="px-2 py-1 rounded-sm bg-muted border border-border text-xs font-mono font-bold">{key}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <UniversalSearch
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onNavigate={(type, item) => {
          if (type === 'client') setActiveBoard('sales');
          else if (type === 'sale') setActiveBoard('sales');
          else if (type === 'purchase') setActiveBoard('purchases');
          else if (type === 'expense') setActiveBoard('expenses');
        }}
      />
    </div>
  );
};

export default Dashboard;
